import { useEffect, useRef, useState } from "react";
import { MotionValue } from "motion/react";
import EventSystem from "../../../../../events/EventSystem";

export type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const useDrawerResize = (
  initialWidth = 400,
  initialHeight = 600,
  x: MotionValue<number>,
  y: MotionValue<number>
) => {
  const [drawerWidth, setDrawerWidth] = useState(initialWidth);
  const [drawerHeight, setDrawerHeight] = useState(initialHeight);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Active resize state refs to avoid stale closures in mousemove
  const resizeState = useRef<{
    direction: ResizeDirection | null;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    startXPos: number;
    startYPos: number;
  }>({
    direction: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    startXPos: 0,
    startYPos: 0,
  });

  const onResizeStart = (direction: ResizeDirection, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = {
      direction,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: drawerWidth,
      startHeight: drawerHeight,
      startXPos: x.get(),
      startYPos: y.get(),
    };
    EventSystem.publish("cadviewer.is_dragging_pane", true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const state = resizeState.current;
      if (!state.direction) return;

      const deltaX = e.clientX - state.startX;
      const deltaY = e.clientY - state.startY;

      const minWidth = 320;
      const maxWidth = window.innerWidth - 40;
      const minHeight = 350;
      const maxHeight = window.innerHeight - 40;

      if (state.direction.includes('w')) {
        let newWidth = state.startWidth - deltaX;
        if (newWidth < minWidth) newWidth = minWidth;
        if (newWidth > maxWidth) newWidth = maxWidth;
        setDrawerWidth(newWidth);
      }

      if (state.direction.includes('e')) {
        let newWidth = state.startWidth + deltaX;
        if (newWidth < minWidth) newWidth = minWidth;
        if (newWidth > maxWidth) newWidth = maxWidth;
        setDrawerWidth(newWidth);
        const actualDeltaX = newWidth - state.startWidth;
        x.set(state.startXPos + actualDeltaX);
      }

      if (state.direction.includes('s')) {
        let newHeight = state.startHeight + deltaY;
        if (newHeight < minHeight) newHeight = minHeight;
        if (newHeight > maxHeight) newHeight = maxHeight;
        setDrawerHeight(newHeight);
      }

      if (state.direction.includes('n')) {
        let newHeight = state.startHeight - deltaY;
        if (newHeight < minHeight) newHeight = minHeight;
        if (newHeight > maxHeight) newHeight = maxHeight;
        setDrawerHeight(newHeight);
        const actualDeltaY = state.startHeight - newHeight;
        y.set(state.startYPos + actualDeltaY);
      }
    };

    const handleMouseUp = () => {
      if (resizeState.current.direction) {
        EventSystem.publish("cadviewer.is_dragging_pane", false);
      }
      resizeState.current.direction = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [x, y]);

  return {
    drawerWidth,
    drawerHeight,
    drawerRef,
    onResizeStart,
  };
};
